import { useQuery } from "@tanstack/react-query"

import { getScopedFilesStatus, ScopedFile } from "~/api/agent-uploads"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"

export interface ScopedFilesStatusResponse {
  success?: boolean
  error?: string | null
  data: {
    files: ScopedFile[]
  }
}

export function useScopedFilesStatus({
  sessionId,
  enabled,
  showMyFilesInRfpResponse,
  pollIntervalMs = 5000
}: {
  sessionId: string
  enabled: boolean
  showMyFilesInRfpResponse?: boolean
  pollIntervalMs?: number
}) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  return useQuery<ScopedFilesStatusResponse>({
    queryKey: ["scoped-files-status", sessionId],
    queryFn: async () => {
      const response = await getScopedFilesStatus({
        tokenInfo: tokenInfo as ITokenInfo,
        sessionId,
        showMyFilesInRfpResponse
      })
      return response.data as ScopedFilesStatusResponse
    },
    enabled: !!(tokenInfo && sessionId && enabled),
    refetchInterval: (query) => {
      const files = query.state.data?.data?.files
      // No data yet, or server hasn't registered the uploaded files — keep polling.
      if (!files || files.length === 0) return pollIntervalMs
      // Poll until every file is either at 100% OR has a non-null error
      // (terminal failure from dpp PrepareSessionScopedFiles — typically
      // "Max retries reached for ingestion" / "Max retries reached for
      // parsing document"). Without the error gate, orphan failed rows
      // from prior picker attempts (still is_active=true but
      // is_ingested=false) keep polling alive forever — see ENG-1249
      // session 9e00d96a... where 2 stuck rows at pct=75 with
      // "Max retries reached" prevented stop even after the two newly
      // attached files reached 100%.
      //
      // Trade-off vs the previous "error can flicker" behavior: SQS does
      // redeliver, so error theoretically clears on later success. In
      // practice errors are only written by `updateWithErrorAndThrow`
      // after the Lambda's internal MAX_RETRIES=3 loop and the queue's
      // maxReceiveCount=2 means at most one further SQS retry — net
      // upper bound is ~6 attempts, all of which have completed by the
      // time the FE sees error≠null. Chips that genuinely fail still
      // get FAILED via the caller's per-file processingTimeoutMs.
      const allTerminal = files.every(
        (file) => file.percentage_completion === 100 || file.error
      )
      return allTerminal ? false : pollIntervalMs
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: 3,
    retryDelay: 5000
  })
}
